/**
 * Clears every browser-side persistence layer for the current origin:
 * service worker registrations, cookies (across the path/subdomain
 * lattice), localStorage, sessionStorage, the Cache API, and IndexedDB
 * databases.
 *
 * Used as a defensive logout cleanup at the signin entry point — when
 * the user lands on `/login`, any leftover state from a previous
 * session is purged before the new auth flow begins.
 *
 * Errors from each subsystem are swallowed because cleanup failure
 * shouldn't block sign-in. Every layer's purge is best-effort.
 */
export function clearAllSiteData() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker
      .getRegistrations()
      .then((registrations: readonly ServiceWorkerRegistration[]) => {
        for (const registration of registrations) {
          registration.unregister()
        }
      })
      .catch(() => {})
  }

  // Cookies — sweep across the path/subdomain matrix. Browsers scope
  // cookies by (name, domain, path), so deleting only the bare name
  // misses cookies set on any nested domain or path.
  const cookies = document.cookie.split('; ')
  for (let c = 0; c < cookies.length; c++) {
    const d = window.location.hostname.split('.')
    while (d.length > 0) {
      // invariant: c < cookies.length; split() always returns at least one element
      const cookieBase =
        encodeURIComponent(cookies[c]!.split(';')[0]!.split('=')[0]!) +
        '=; expires=Thu, 01-Jan-1970 00:00:01 GMT; domain=' +
        d.join('.') +
        ' ;path='
      const p = location.pathname.split('/')
      document.cookie = cookieBase + '/'
      while (p.length > 0) {
        document.cookie = cookieBase + p.join('/')
        p.pop()
      }
      d.shift()
    }
  }

  localStorage.clear()
  sessionStorage.clear()

  if ('caches' in window) {
    caches
      .keys()
      .then((keyList: string[]) => Promise.all(keyList.map((key: string) => caches.delete(key))))
      .catch(() => {})
  }

  // Firefox guard: `indexedDB.databases()` is not implemented there yet.
  // The previous `databases?.()` short-circuits the call but the subsequent
  // `.then()` was being invoked on `undefined`, throwing a synchronous
  // TypeError that the chained `.catch()` cannot intercept (it's on the
  // promise chain, not around the throw). Guard explicitly with a
  // function-type check before calling.
  if ('indexedDB' in window && typeof indexedDB.databases === 'function') {
    indexedDB
      .databases()
      .then((dbs) => {
        dbs.forEach((db) => {
          if (db.name) indexedDB.deleteDatabase(db.name)
        })
      })
      .catch(() => {})
  }
}
